import SkeletonLoader from "@/components/layout/skeleton-loader/skeleton-loader";
import { motion } from "framer-motion";
import styles from "./checkout-token.module.scss";
import RemoveBtn from "@/features/bridge/components/remove-btn/remove-btn";
import { useMultiTokenManager } from "@/features/bridge/stores/multi-token-manager";
import { useNftTokenManager } from "@/features/bridge/stores/nft-token-manager";
import { useTokenManagerStore } from "@/features/bridge/stores/token-type.store";
import type { ModifiedNftToken } from "@/features/bridge/types/open-sea-api.types";
import { TokenType } from "@/features/bridge/types/token-selector.types";
import { getMultiTokenBackgroundColor } from "@/utils/token-color.util";
import { useMemo, useState, useEffect } from "react";
import { useBridge } from "@/features/bridge/providers/bridge.provider";
import { generateImageFallbacks, hasFastImageFallback } from "@/utils/nft-image-fallback.util";


type Props = {
  token: ModifiedNftToken;
};

// Small checkout image component with fallback support (40x40px)
const CheckoutTokenImage = ({
  token,
  chainId
}: {
  token: ModifiedNftToken;
  chainId?: number;
}) => {
  const [imageUrl, setImageUrl] = useState<string | null>(token.display_image_url || null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const colorConfig = useMemo(
    () => getMultiTokenBackgroundColor(token.address, token.identifier, token.symbol),
    [token.address, token.identifier, token.symbol]
  );

  useEffect(() => {
    const generateFallback = async () => {
      if (!token.display_image_url && token.address && token.identifier && chainId) {
        // Check if we'll have a fast result (cached or non-contract)
        const willBeFast = hasFastImageFallback(
          token.display_image_url,
          token.address,
          token.identifier,
          chainId,
          token.token_standard
        );

        // Only show loading for slow operations (contract calls)
        if (!willBeFast) {
          setIsLoading(true);
        }

        try {
          const fallbacks = await generateImageFallbacks(
            token.display_image_url,
            token.symbol,
            token.identifier,
            token.address,
            chainId,
            token.token_standard
          );
          if (fallbacks.length > 0) {
            setImageUrl(fallbacks[0]);
          }
        } catch (error) {
          console.warn('Failed to generate checkout token image fallback:', error);
        }
        setIsLoading(false);
      }
    };

    generateFallback();
  }, [token.display_image_url, token.symbol, token.identifier, token.address, chainId]);

  const handleError = () => {
    setHasError(true);
  };

  const handleLoad = () => {
    setHasError(false);
  };

  if (isLoading) {
    return (
      <div style={{ width: 40, height: 40 }}>
        <SkeletonLoader />
      </div>
    );
  }

  if (imageUrl && !hasError) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={imageUrl}
        alt={token?.name || "checkout-token"}
        width={40}
        height={40}
        style={{ objectFit: 'cover', borderRadius: '3px' }}
        onError={handleError}
        onLoad={handleLoad}
        loading="lazy"
        draggable={false}
      />
    );
  }

  // Fallback to colored background with text
  if (colorConfig.gravatarUrl) {
    return (
      <div
        style={{
          width: 40,
          height: 40,
          backgroundColor: colorConfig.backgroundColor,
          borderRadius: '3px',
          position: 'relative'
        }}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={colorConfig.gravatarUrl}
          alt={`Token ${token.identifier} pattern`}
          style={{
            borderRadius: '3px',
            objectFit: 'cover',
            width: '100%',
            height: '100%',
            position: 'absolute',
            top: 0,
            left: 0
          }}
          loading="lazy"
          draggable={false}
        />
      </div>
    );
  }

  return (
    <div
      style={{
        width: 40,
        height: 40,
        backgroundColor: colorConfig.backgroundColor,
        borderRadius: '3px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '8px',
        fontWeight: 700,
        color: 'white',
        textShadow: '0 0 4px rgba(0,0,0,0.8)'
      }}
    >
      {token.symbol && <div style={{ marginBottom: '1px' }}>{token.symbol}</div>}
      <div>{token.identifier}</div>
    </div>
  );
};

const CheckoutMultiToken = ({ token }: Props) => {
  const { currentlySelectedTokenType } = useTokenManagerStore();
  const { toggleToken: toggleNft } = useNftTokenManager();
  const {
    toggleToken: toggleMulti,
    getToken,
    getTokenByCollection,
    changeTokenAmount,
    tokenIncrement,
    tokenDecrement,
  } = useMultiTokenManager();
  const { bridgeControls } = useBridge();
  const isMulti = currentlySelectedTokenType === TokenType.MULTI;
  const toggleToken = isMulti ? toggleMulti : toggleNft;
  // Use more specific token retrieval to avoid identifier collisions
  const selectedTokenAmount = getTokenByCollection(token.identifier, token.collection)?.amountToBridge ||
                              getToken(token.identifier)?.amountToBridge;

  // Get color configuration for gravatar fallback
  const colorConfig = useMemo(() =>
    getMultiTokenBackgroundColor(token.address, token.identifier, token.symbol),
    [token.address, token.identifier, token.symbol]
  );

  return (
    <motion.div
      initial={{ translateX: -10, opacity: 0 }}
      animate={{ translateX: 0, opacity: 1 }}
      className={styles.checkoutTokenContainer}
    >
      <div className={styles.image} style={{ position: 'relative' }}>
        <CheckoutTokenImage
          token={token}
          chainId={bridgeControls.fromChain.originChain?.chainId}
        />
      </div>
      <p>
        {token?.name ? token.name : token.identifier}
        {token?.name && ` #${token.identifier}`}
      </p>

      {/* ONLY SHOW IF MULTI TOKEN IS ACTIVE  */}
      {isMulti && <b>x{selectedTokenAmount}</b>}
      {/* {isMulti && (
        <StepperInput
          size="small"
          value={selectedTokenAmount!}
          changeTokenAmount={(amount) => changeTokenAmount(token, amount)}
          incrementToken={() => tokenIncrement(token)}
          decrementToken={() => tokenDecrement(token)}
          handleMaxAmount={() => changeTokenAmount(token, token.availableTokens!)}
        />
      )} */}
      <div className={styles.removeBtn}>
        <RemoveBtn handleFunction={() => toggleToken(token)} />
      </div>
    </motion.div>
  );
};

export default CheckoutMultiToken;
